import { useQueryClient } from 'react-query';
import { useCreateCreditType } from '../../../../../../../hooks/requests/use-create-credit-type';
import { useUpdateCreditType } from '../../../../../../../hooks/requests/use-update-credit-type';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { invalidateProductsQueries } from '../../../../../../../utils/query-invalidation';
import { isNotNullOrUndefined } from '@vy/utils';
import type { CreditType } from '@vy/api-definition';
import type { UpsertCreditTypeFormData } from '../upsert-credit-type-form-schema';

type UseCreditTypeSubmissionProps = {
  onSuccess?: (productName: string) => void;
  onCreditTypeCreated?: (creditType: CreditType) => void;
  onClose: () => void;
  resetCallerState: () => void;
  editCreditType?: CreditType;
};

export const useCreditTypeSubmission = ({
  onSuccess,
  onCreditTypeCreated,
  onClose,
  resetCallerState,
  editCreditType,
}: UseCreditTypeSubmissionProps) => {
  const queryClient = useQueryClient();
  const createCreditType = useCreateCreditType();
  const updateCreditType = useUpdateCreditType();
  const isEditMode = isNotNullOrUndefined(editCreditType);

  const submitCreditType = async (data: UpsertCreditTypeFormData) => {
    const onSuccessHandler = async () => {
      await invalidateProductsQueries(queryClient);

      if (onSuccess) {
        onSuccess(data.name);
      }

      onClose();
      resetCallerState();
    };

    const onCreateSuccessHandler = async (response: { creditType: CreditType }) => {
      await invalidateProductsQueries(queryClient);

      if (onSuccess) {
        onSuccess(response.creditType.name);
      }

      if (onCreditTypeCreated) {
        onCreditTypeCreated(response.creditType);
      }

      onClose();
      resetCallerState();
    };

    if (isEditMode && isNotNullOrUndefined(editCreditType)) {
      await runMutation(
        updateCreditType,
        { creditTypeId: editCreditType.id, updateCreditTypeRequest: data },
        {
          pending: 'Updating credit product...',
          success: 'Credit product updated successfully',
          error: (toastProps) => (toastProps?.data as Error)?.message || 'Failed to update credit product',
        },
        { onSuccess: onSuccessHandler },
      );
    } else {
      await runMutation(
        createCreditType,
        data,
        {
          pending: 'Creating credit product...',
          success: 'Credit product created successfully',
          error: (toastProps) => (toastProps?.data as Error)?.message || 'Failed to create credit product',
        },
        { onSuccess: onCreateSuccessHandler },
      );
    }
  };

  return { submitCreditType };
};
